import { FileText, Lightbulb, Pen, Search, BookOpen, Rocket } from 'lucide-react';
import SeoHead from '../components/SeoHead';

const STEPS = [
  { icon: Lightbulb, step: '01', title: 'Share Your Vision', desc: 'Tell us about your book idea, genre, target audience, and any specific requirements. We will work with your vision.' },
  { icon: Search, step: '02', title: 'Research & Outline', desc: 'Our team researches your topic and creates a detailed chapter-by-chapter outline for your approval.' },
  { icon: Pen, step: '03', title: 'AI-Powered Writing', desc: 'Our AI generates multiple draft versions while our editors ensure quality, consistency, and your unique voice.' },
  { icon: FileText, step: '04', title: 'Review & Revise', desc: 'You review each chapter and provide feedback. We revise until it is exactly right.' },
  { icon: BookOpen, step: '05', title: 'Format & Design', desc: 'Professional formatting for Kindle KDP, IngramSpark, or any platform. Cover design included in higher tiers.' },
  { icon: Rocket, step: '06', title: 'Publish & Launch', desc: 'We guide you through publishing on Amazon and other platforms, plus marketing strategies to launch your book.' },
];

export default function GhostwriterHowItWorks() {
  return (
    <>
      <SeoHead
        title="How Book Ghostwriting Works — 6-Step Process | AI Ghostwriter Pro"
        description="Learn how our ghostwriting process works in 6 simple steps. From sharing your book idea to holding your published manuscript. Hire a professional ghostwriter with transparent pricing and full copyright ownership."
        keywords="how does ghostwriting work, ghostwriting process, steps to hire a ghostwriter, book ghostwriting process, how to get a book ghostwritten, ghostwriter workflow, professional ghostwriting steps"
        path="/how-it-works"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "How to Get Your Book Ghostwritten by AI Ghostwriter Pro",
            "description": "Our 6-step ghostwriting process from idea to published book",
            "step": [
              {"@type": "HowToStep", "position": 1, "name": "Share Your Vision", "text": "Tell us about your book idea, genre, target audience, and goals"},
              {"@type": "HowToStep", "position": 2, "name": "Strategy & Outline", "text": "We create a detailed outline and chapter structure for your approval"},
              {"@type": "HowToStep", "position": 3, "name": "Writing Begins", "text": "Our AI-powered ghostwriting team crafts your manuscript"},
              {"@type": "HowToStep", "position": 4, "name": "Review & Revisions", "text": "You review drafts and request revisions until satisfied"},
              {"@type": "HowToStep", "position": 5, "name": "Professional Formatting", "text": "Your manuscript is professionally formatted for publishing"},
              {"@type": "HowToStep", "position": 6, "name": "Delivery & Publishing", "text": "Receive your completed book ready for Amazon KDP and more"}
            ]
          }
        ]}
      />

      <section className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-20">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">Our Process</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight mb-6">How It <span className="text-primary">Works</span></h1>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">From your initial idea to a published book, our streamlined 6-step process makes ghostwriting simple and transparent.</p>
          </div>

          <div className="space-y-0">
            {STEPS.map((s, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center py-12 border-b border-white/5 last:border-0" data-testid={`step-${i}`}>
                <div className="md:col-span-1">
                  <span className="font-serif text-4xl text-primary/30">{s.step}</span>
                </div>
                <div className="md:col-span-2 flex justify-center">
                  <div className="w-16 h-16 rounded-sm bg-white/5 border border-white/10 flex items-center justify-center">
                    <s.icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="md:col-span-9">
                  <h3 className="font-serif text-2xl mb-3 text-foreground">{s.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-20">
            <a href="/pricing" data-testid="hiw-cta" className="inline-flex items-center gap-2 rounded-sm px-8 py-4 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black hover:shadow-[0_0_25px_rgba(255,255,255,0.5)]">
              Choose Your Package
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
